import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Button,
  Grid,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  RadioGroup,
  Radio,
  FormLabel,
  Snackbar
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ComputerIcon from '@mui/icons-material/Computer';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LockIcon from '@mui/icons-material/Lock';
import { StatePreference } from '../types/availability';
import { usStates } from '../utils/usStates';
import { preferencesApi } from '../utils/preferencesApi';

const AvailabilityPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<StatePreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [selectedNewState, setSelectedNewState] = useState<string>('');
  const [editable, setEditable] = useState<boolean>(true);

  // Load existing preferences
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const data = await preferencesApi.getPreferences();
      setPreferences(data?.statePreferences || []);
      setEditable(data?.editable ?? true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to load preferences');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error loading preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const autoSave = async (updatedPreferences: StatePreference[]) => {
    try {
      // Filter out states with both virtual and in-person set to false before saving
      const filteredPreferences = updatedPreferences.filter(p => p.virtualAvailable || p.inPersonAvailable);
      const result = await preferencesApi.setPreferences({ statePreferences: filteredPreferences });

      if (result.code === 'PREFERENCES_LOCKED') {
        setEditable(false);
        setSnackbarMessage('Preferences are locked. Contact the scheduling team to make changes.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        await loadPreferences();
        return;
      }

      setPreferences(filteredPreferences);
      if (typeof result.editable === 'boolean') setEditable(result.editable);

      setSnackbarMessage('Preferences saved automatically!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to save preferences');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error saving preferences:', err);
    }
  };

  const normalizePrimary = (list: StatePreference[]): StatePreference[] => {
    if (list.length === 0) return [];
    const primaryIdx = list.findIndex(p => p.isPrimary === true);
    const chosen = primaryIdx >= 0 ? primaryIdx : 0;
    return list.map((p, i) => ({ ...p, isPrimary: i === chosen }));
  };

  const setAsPrimary = (stateAbbr: string) => {
    if (!editable) return;
    const updated = preferences.map(p => ({ ...p, isPrimary: p.state === stateAbbr }));
    setPreferences(updated);
    autoSave(updated);
  };

  const handleAvailabilityChange = (stateAbbr: string, type: 'virtual' | 'inPerson', value: string) => {
    if (!editable) return;
    const isAvailable = value === 'yes';

    setPreferences(prev => {
      const existingIndex = prev.findIndex(p => p.state === stateAbbr);
      let updatedPreferences: StatePreference[];
      
      if (existingIndex >= 0) {
        // Update existing preference
        const updated = [...prev];
        if (type === 'virtual') {
          updated[existingIndex] = {
            ...updated[existingIndex],
            virtualAvailable: isAvailable
          };
        } else {
          updated[existingIndex] = {
            ...updated[existingIndex],
            inPersonAvailable: isAvailable
          };
        }
        
        // Keep the state in the list even if both are false
        // It will be filtered out during save
        updatedPreferences = updated;
      } else {
        // Add new preference
        const newPreference: StatePreference = {
          state: stateAbbr,
          virtualAvailable: type === 'virtual' ? isAvailable : false,
          inPersonAvailable: type === 'inPerson' ? isAvailable : false
        };
        updatedPreferences = [...prev, newPreference];
      }
      
      // Auto-save after updating preferences
      autoSave(updatedPreferences);
      
      return updatedPreferences;
    });
  };

  const addNewState = () => {
    if (!editable) return;
    if (!selectedNewState || preferences.find(p => p.state === selectedNewState)) {
      return;
    }

    const isFirst = preferences.length === 0;
    const newPreference: StatePreference = {
      state: selectedNewState,
      virtualAvailable: true,
      inPersonAvailable: true,
      isPrimary: isFirst,
    };

    const updatedPreferences = normalizePrimary([...preferences, newPreference]);
    setPreferences(updatedPreferences);
    setSelectedNewState('');

    autoSave(updatedPreferences);
  };

  const removeState = (stateAbbr: string) => {
    if (!editable) return;
    const updatedPreferences = normalizePrimary(preferences.filter(p => p.state !== stateAbbr));
    setPreferences(updatedPreferences);

    autoSave(updatedPreferences);
  };

  const getAvailableStates = () => {
    const configuredStates = new Set(preferences.map(p => p.state));
    return usStates.filter(state => !configuredStates.has(state.abbr));
  };

  const getStateName = (stateAbbr: string): string => {
    const state = usStates.find(s => s.abbr === stateAbbr);
    return state ? state.name : stateAbbr;
  };


  // Helper function to check if a state will be removed on save
  const isStateMarkedForRemoval = (preference: StatePreference): boolean => {
    return !preference.virtualAvailable && !preference.inPersonAvailable;
  };

  // Filter out states that will be removed for counting
  const activePreferences = preferences.filter(p => !isStateMarkedForRemoval(p));
  const selectedStatesCount = activePreferences.length;
  const virtualStatesCount = activePreferences.filter(p => p.virtualAvailable).length;
  const inPersonStatesCount = activePreferences.filter(p => p.inPersonAvailable).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading preferences...</Typography>
      </Box>
    );
  }

  return (
    <Box>

      <Typography variant="h5" component="h2" gutterBottom>
        Availability Preferences
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Select the states where you&apos;re available to work. You can choose different availability
        for virtual and in-person depositions based on your certifications and qualifications.
        Mark one state as your <strong>Primary</strong> — this is the state where you most often work.
      </Typography>

      {!editable && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
          Your state preferences are locked. Please contact the scheduling team to coordinate any changes.
        </Alert>
      )}

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center"
          sx={{ 
            flexWrap: 'wrap',
            gap: 1,
            '& > *': { mb: 1 }
          }}
        >
          <Typography variant="body2">
            <strong>Summary:</strong>
          </Typography>
          <Chip 
            label={`${selectedStatesCount} ${selectedStatesCount === 1 ? 'state' : 'states'} selected`} 
            size="small" 
            color="primary" 
          />
          <Chip 
            icon={<ComputerIcon />}
            label={`${virtualStatesCount} virtual`} 
            size="small" 
            color="info" 
          />
          <Chip 
            icon={<PersonIcon />}
            label={`${inPersonStatesCount} in-person`} 
            size="small" 
            color="secondary" 
          />
        </Stack>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Add New State */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add State
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }} disabled={!editable}>
            <InputLabel>Select State</InputLabel>
            <Select
              value={selectedNewState}
              onChange={(e) => setSelectedNewState(e.target.value)}
              label="Select State"
              size="small"
            >
              {getAvailableStates().map((state) => (
                <MenuItem key={state.abbr} value={state.abbr}>
                  {state.abbr} - {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addNewState}
            disabled={!selectedNewState || !editable}
          >
            Add State
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Configured States */}
      {preferences.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No states configured yet. Use the dropdown above to add your first state.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {preferences.map((preference) => {
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={preference.state}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    height: '100%',
                    border: 2,
                    borderColor: isStateMarkedForRemoval(preference) ? 'warning.main' : 'primary.main',
                    bgcolor: isStateMarkedForRemoval(preference) ? 'warning.50' : 'primary.50',
                    transition: 'all 0.2s ease-in-out',
                    position: 'relative',
                    opacity: isStateMarkedForRemoval(preference) ? 0.7 : 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                      <Typography variant="subtitle1" component="div">
                        <strong>{preference.state}</strong> - {getStateName(preference.state)}
                      </Typography>
                      {preference.isPrimary && (
                        <Chip
                          icon={<StarIcon sx={{ fontSize: '0.9rem' }} />}
                          label="Primary"
                          size="small"
                          color="warning"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {!preference.isPrimary && (
                        <IconButton
                          size="small"
                          onClick={() => setAsPrimary(preference.state)}
                          disabled={!editable}
                          title="Set as primary state"
                          sx={{ mt: -0.5 }}
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => removeState(preference.state)}
                        disabled={!editable}
                        sx={{ mt: -0.5, mr: -0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {isStateMarkedForRemoval(preference) && (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: '0.75rem' }}>
                      This state will be removed when you save your preferences since both options are &quot;No&quot;.
                    </Alert>
                  )}
                  
                  <Stack spacing={2}>
                    <FormControl component="fieldset" disabled={!editable}>
                      <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
                        <ComputerIcon fontSize="small" color="info" />
                        Virtual Availability
                      </FormLabel>
                      <RadioGroup
                        row
                        value={preference.virtualAvailable ? 'yes' : 'no'}
                        onChange={(e) => handleAvailabilityChange(preference.state, 'virtual', e.target.value)}
                        sx={{ mt: 0.5 }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio size="small" color="success" />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio size="small" color="error" />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                    
                    <FormControl component="fieldset" disabled={!editable}>
                      <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
                        <PersonIcon fontSize="small" color="secondary" />
                        In-Person Availability
                      </FormLabel>
                      <RadioGroup
                        row
                        value={preference.inPersonAvailable ? 'yes' : 'no'}
                        onChange={(e) => handleAvailabilityChange(preference.state, 'inPerson', e.target.value)}
                        sx={{ mt: 0.5 }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio size="small" color="success" />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio size="small" color="error" />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}


      {/* Help Text */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
        <Typography variant="body2" color="info.main">
          <strong>Example:</strong> If you live in New Jersey but only certified to work in Pennsylvania, 
          you might select Pennsylvania for both virtual and in-person work but you would not select New Jersey for either.
        </Typography>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AvailabilityPreferences;